import { useEffect, useRef, useState } from "react";
import { productImage } from "@/mocks/landingData";

const benefits = [
  { icon: "ri-run-line", label: "Mobility" },
  { icon: "ri-drop-line", label: "Joint Lubrication" },
  { icon: "ri-heart-pulse-line", label: "Healthy Aging" },
  { icon: "ri-body-scan-line", label: "Flexibility" },
];

export default function FormulaSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-16 md:py-24 bg-background-50">
      <div className="w-full max-w-6xl mx-auto px-4 md:px-8 lg:px-12">
        <div
          className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-20 transition-all duration-600 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)" }}
        >
          <div className="lg:w-1/2">
            <div className="rounded-2xl bg-background-100 border border-background-200/70 p-6 md:p-8">
              <img
                src={productImage}
                alt="Joint Genesis Supplement Bottle"
                className="w-full max-w-sm mx-auto h-auto object-contain hover-img-zoom"
                width={400}
                height={600}
              />
              <div className="text-center mt-4">
                <p className="text-foreground-500 text-base font-medium tracking-wide">
                  DIETARY SUPPLEMENT | 30 CAPSULES
                </p>
              </div>
            </div>
          </div>

          <div className="lg:w-1/2">
            <p className="text-accent-500 text-base font-medium tracking-[0.2em] uppercase mb-3">
              The Formula
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground-950 leading-tight mb-6">
              Joint Genesis
            </h2>
            <p className="text-foreground-700 text-xl leading-relaxed mb-8">
              A doctor-formulated, once-daily blend designed around the
              structures that keep joints comfortable and mobile.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-8">
              {benefits.map((b, idx) => (
                <div
                  key={b.label}
                  className={`flex items-center gap-3 p-4 rounded-lg bg-background-100 border border-background-200/70 card-hover-lift transition-all duration-600 ${
                    visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"
                  }`}
                  style={{
                    transitionDelay: `${idx * 80}ms`,
                    transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)",
                  }}
                >
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                    <i className={`${b.icon} text-base`} />
                  </div>
                  <span className="text-foreground-800 text-lg font-medium">{b.label}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 text-lg text-foreground-600">
              <i className="ri-shield-check-line text-primary-500" />
              <span>Doctor-Formulated</span>
              <span className="mx-2">|</span>
              <i className="ri-flask-line text-primary-500" />
              <span>Premium Ingredients</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}